"use client";

import { useRef, useState } from "react";
import {
  Checkbox,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL } from "@/components/tool-kit/styles";
import {
  fontAttrs,
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { EntriesGrid } from "@/components/tools/printables/reading-log-entries";
import { LogTable } from "@/components/tools/printables/reading-log-page";
import { MUTED, SheetHeader } from "@/components/tools/printables/sheet-header";
import { useToolUi } from "@/components/tools/tool-ui";
import { layoutTable } from "@/lib/tool-kit/printables/attendance";
import { ACCENTS, type AccentKey } from "@/lib/tool-kit/printables/certificate";
import {
  type Orientation,
  type PaperSize,
  pageGeometry,
} from "@/lib/tool-kit/printables/page";
import {
  columnTotal,
  LOG_COLUMNS,
  LOG_PRESETS,
  type LogColumn,
  type LogEntries,
  logColumns,
  MAX_LOG_ROWS,
  ROW_HEIGHTS,
  type RowSize,
  rowHeightFor,
} from "@/lib/tool-kit/printables/reading-log";

/** English strings. A translated content file overrides these through `ui`. */
export const READING_LOG_TEMPLATE_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  presetLabel: "Start from",
  presets: {
    custom: "Custom columns",
    nightly: "Nightly log — date, book, pages, minutes, parent initials",
    book: "Book log — date, book, author, pages, rating",
    response: "Reading response — date, book, pages, response, rating",
  },
  columnsLabel: "Columns",
  heads: {
    date: "Date",
    title: "Book title",
    author: "Author",
    pages: "Pages read",
    minutes: "Minutes",
    rating: "Rating",
    response: "What I thought",
    initials: "Parent initials",
  },
  titleLabel: "Title",
  defaultTitle: "Reading Log",
  goalLabel: "Reading goal (optional)",
  goalHint: "Printed at the top right, for example: 20 minutes a night.",
  goalPrefix: "Goal",
  nameLine: "Name and teacher lines",
  nameLabel: "Name",
  teacherLabel: "Teacher",
  totalRow: "Totals row",
  totalLabel: "Total",
  rowsLabel: "Rows",
  rowsFill: "Fill the page",
  rowSizeLabel: "Row height",
  rowSizes: {
    compact: "Compact — 7 mm",
    standard: "Standard — 9 mm",
    roomy: "Roomy — 12 mm",
  },
  accentLabel: "Colour for the stars",
  accents: {
    navy: "Navy",
    burgundy: "Burgundy",
    forest: "Forest green",
    gold: "Antique gold",
    purple: "Purple",
    black: "Black and white",
  },
  fillLabel: "Fill in the first rows on screen before printing",
  fillHint:
    "What you type is printed into the sheet. It is not saved or uploaded.",
  cellLabel: "{column}, row {row}",
  ratingNone: "No stars",
  clearEntries: "Clear the entries",
  previewLabel: "Reading log preview",
} as const;

type PresetKey = keyof typeof LOG_PRESETS;
type PresetChoice = PresetKey | "custom";

const ROW_CHOICES = ["0", "10", "15", "20", "25", "30", "40"];
const HEADER_HEIGHT = 24;
const TABLE_HEADER = 9;

export default function ReadingLogTemplateWidget() {
  const strings = useToolUi(READING_LOG_TEMPLATE_STRINGS);
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [preset, setPreset] = useState<PresetChoice>("nightly");
  const [enabled, setEnabled] = useState<ReadonlySet<LogColumn>>(
    new Set(LOG_PRESETS.nightly)
  );
  const [title, setTitle] = useState<string>(strings.defaultTitle);
  const [goal, setGoal] = useState("");
  const [nameLine, setNameLine] = useState(true);
  const [totalRow, setTotalRow] = useState(true);
  const [rows, setRows] = useState("0");
  const [rowSize, setRowSize] = useState<RowSize>("standard");
  const [accent, setAccent] = useState<AccentKey>("navy");
  const [fill, setFill] = useState(false);
  const [entries, setEntries] = useState<LogEntries>({});
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [orientation, setOrientation] = useState<Orientation>("portrait");

  function choosePreset(key: PresetChoice) {
    setPreset(key);
    if (key !== "custom") {
      setEnabled(new Set(LOG_PRESETS[key]));
    }
  }

  function toggle(key: LogColumn, on: boolean) {
    setPreset("custom");
    setEnabled((previous) => {
      const next = new Set(previous);
      if (on) {
        next.add(key);
      } else if (next.size > 1) {
        next.delete(key);
      }
      return next;
    });
  }

  function setEntry(row: number, key: LogColumn, value: string) {
    setEntries((previous) => ({
      ...previous,
      [row]: { ...previous[row], [key]: value },
    }));
  }

  const geometry = pageGeometry(paper, orientation);
  const columns = logColumns(geometry, enabled);
  const rowHeight = rowHeightFor(rowSize, enabled.has("response"));
  const reserved = HEADER_HEIGHT + TABLE_HEADER + (totalRow ? rowHeight : 0);
  const fillRows = Math.max(
    1,
    Math.floor((geometry.contentHeight - reserved) / rowHeight)
  );
  const rowCount = Math.min(MAX_LOG_ROWS, Number(rows) || fillRows);
  const layout = layoutTable(rowCount + (totalRow ? 1 : 0), geometry, {
    headerHeight: HEADER_HEIGHT,
    tableHeader: TABLE_HEADER,
    minRow: rowHeight,
    maxRow: rowHeight,
    footer: 0,
  });
  const pageTotals = totalRow
    ? {
        label: strings.totalLabel,
        pages: String(columnTotal(entries, "pages", rowCount) ?? ""),
        minutes: String(columnTotal(entries, "minutes", rowCount) ?? ""),
      }
    : null;
  const right = geometry.left + geometry.contentWidth;
  const pages = layout.pages.map((page, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: sheets are positional
    <g key={`log-${index}`}>
      {page.first ? (
        <>
          <SheetHeader
            fields={
              nameLine
                ? [
                    { label: strings.nameLabel, share: 5 },
                    { label: strings.teacherLabel, share: 4 },
                  ]
                : []
            }
            geometry={geometry}
            measure={measure}
            title={title.trim()}
          />
          {goal.trim() ? (
            <text
              fill={MUTED}
              fontSize={4.4}
              textAnchor="end"
              x={right}
              y={geometry.top + 6}
              {...fontAttrs({ kind: "sans" })}
            >
              {`${strings.goalPrefix}: ${goal.trim()}`}
            </text>
          ) : null}
        </>
      ) : null}
      <LogTable
        accent={ACCENTS[accent]}
        columns={columns}
        entries={entries}
        heads={strings.heads}
        measure={measure}
        page={page}
        right={right}
        rowCount={rowCount}
        rowHeight={layout.rowHeight}
        tableHeader={TABLE_HEADER}
        totals={pageTotals}
      />
    </g>
  ));

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="rl-preset"
            label={strings.presetLabel}
            onChange={choosePreset}
            options={(["nightly", "book", "response", "custom"] as const).map(
              (value) => ({ value, label: strings.presets[value] })
            )}
            value={preset}
          />
          <TextField
            id="rl-title"
            label={strings.titleLabel}
            onChange={setTitle}
            value={title}
          />
          <TextField
            hint={strings.goalHint}
            id="rl-goal"
            label={strings.goalLabel}
            onChange={setGoal}
            value={goal}
          />
          <SelectField
            id="rl-rows"
            label={strings.rowsLabel}
            onChange={setRows}
            options={ROW_CHOICES.map((value) => ({
              value,
              label: value === "0" ? strings.rowsFill : value,
            }))}
            value={rows}
          />
          <SelectField
            id="rl-row-size"
            label={strings.rowSizeLabel}
            onChange={setRowSize}
            options={(Object.keys(ROW_HEIGHTS) as RowSize[]).map((value) => ({
              value,
              label: strings.rowSizes[value],
            }))}
            value={rowSize}
          />
          <SelectField
            id="rl-accent"
            label={strings.accentLabel}
            onChange={setAccent}
            options={(Object.keys(ACCENTS) as AccentKey[]).map((value) => ({
              value,
              label: strings.accents[value],
            }))}
            value={accent}
          />
          <PaperControls
            idPrefix="rl"
            onOrientation={setOrientation}
            onPaper={setPaper}
            orientation={orientation}
            paper={paper}
            strings={strings}
          />
        </div>
        <fieldset className="space-y-2">
          <legend className={LABEL}>{strings.columnsLabel}</legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {LOG_COLUMNS.map((key) => (
              <Checkbox
                checked={enabled.has(key)}
                key={key}
                label={strings.heads[key]}
                onChange={(on) => toggle(key, on)}
              />
            ))}
          </div>
        </fieldset>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={nameLine}
            label={strings.nameLine}
            onChange={setNameLine}
          />
          <Checkbox
            checked={totalRow}
            label={strings.totalRow}
            onChange={setTotalRow}
          />
          <Checkbox
            checked={fill}
            label={strings.fillLabel}
            onChange={setFill}
          />
        </div>
        {fill ? (
          <div className="space-y-2">
            <p className="text-muted-foreground text-xs">{strings.fillHint}</p>
            <EntriesGrid
              cellLabel={strings.cellLabel}
              columns={LOG_COLUMNS.filter((key) => enabled.has(key))}
              entries={entries}
              heads={strings.heads}
              onChange={setEntry}
              ratingNone={strings.ratingNone}
              rows={Math.min(rowCount, MAX_LOG_ROWS)}
            />
            <button
              className={BUTTON_SMALL}
              onClick={() => setEntries({})}
              type="button"
            >
              {strings.clearEntries}
            </button>
          </div>
        ) : null}
        <PrintBar
          fileName={`reading-log-${paper}`}
          pageTotal={pages.length}
          paper={paper}
          strings={strings}
          svgRef={svgRef}
        />
      </div>
      <PrintablePages
        className={orientation === "landscape" ? "max-w-3xl" : "max-w-xl"}
        geometry={geometry}
        label={strings.previewLabel}
        pages={pages}
        svgRef={svgRef}
      />
    </div>
  );
}
